<template>
  <div class="gs-asset-type">
    <asset-segmented
      :items="allArticlesTabs"
      :current="allArticlesTab"
      :error-values="allArticlesErrorTabs"
      label="Articles and press releases"
      @select="handleAllArticlesTabChange"
    />

    <div v-if="allArticlesTab === 'pressReleases'" class="gs-asset-split">
      <template v-for="(release, index) in pressReleases">
        <section
          v-if="isPressReleaseTabActive(release)"
          :key="release.id"
          class="gs-asset-editor"
        >
          <header class="gs-asset-editor__head">
            <div class="gs-asset-editor__head-fields">
              <gs-field :label="$t('frontend_resources_name')">
                <text-input
                  v-model="release.name"
                  :validator="$v.localFunnelResource.press_releases.$each[index].name"
                  :attribute="$t('frontend_groovesell_press_releases_name')"
                  :placeholder="$t('frontend_resources_name')"
                />
              </gs-field>
            </div>
            <div class="gs-asset-editor__controls">
              <span class="gs-asset-editor__toggle">
                <span>Show to affiliates</span>
                <gd-toggle v-model="release.enabled" :value="release.enabled" />
              </span>
              <button
                type="button"
                class="gs-asset-editor__delete"
                data-action="delete-press-release"
                title="Delete press release"
                aria-label="Delete press release"
                @click="openConfirmModal(index, 'deletePressRelease')"
              >
                <trash-icon class="w-4 h-4 fill-current" />
              </button>
            </div>
          </header>

          <div class="gs-asset-editor__section">
            <field-error
              :validator="$v.localFunnelResource.press_releases.$each[index].press_release"
              :attribute="$t('frontend_resources_press_release')"
            >
              <template #default="{ hasErrors, message }">
                <gs-field :label="$t('frontend_resources_press_release')" :error="message">
                  <textarea
                    v-model="release.press_release"
                    class="gs-textarea"
                    :class="{ 'has-error': hasErrors }"
                    :placeholder="$t('frontend_resources_press_release')"
                  ></textarea>
                </gs-field>
              </template>
            </field-error>
            <div class="gs-asset-editor__foot">
              <span class="gs-asset-editor__count">{{ characterCount(release.press_release) }}</span>
              <gs-name-select placeholder="Insert" max-height="min(280px, 40vh)" :actions="pressReleaseInsertActions(index)" size="action" />
            </div>
          </div>
        </section>
      </template>

      <!-- Only shown when there is something to pick: with an empty list the
           list's own empty state is the single message. -->
      <div v-if="hasPressReleases && !selectedPressRelease" class="gs-asset-empty">
        <p>Select a press release from the list to edit it.</p>
      </div>
      <asset-item-list
        title="Press releases"
        :items="pressReleases"
        :selected-id="selectedPressReleaseId"
        :add-label="$t('frontend_resources_add_new')"
        name-fallback="Untitled press release"
        :empty-copy="$t('frontend_resources_you_dont_have_any_press_release')"
        enabled-key="enabled"
        :error-ids="pressReleasesErrorIds"
        @select="selectPressRelease"
        @add="createPressRelease()"
        @reorder="reorderPressReleases"
      />
    </div>

    <div v-else-if="allArticlesTab === 'articles'" class="gs-asset-split">
      <template v-for="(article, index) in articles">
        <section
          v-if="isArticleTabActive(article)"
          :key="article.id"
          class="gs-asset-editor"
        >
          <header class="gs-asset-editor__head">
            <div class="gs-asset-editor__head-fields">
              <gs-field :label="$t('frontend_resources_name')">
                <text-input
                  v-model="article.name"
                  :validator="$v.localFunnelResource.articles.$each[index].name"
                  :attribute="$t('frontend_groovesell_article_name')"
                  :placeholder="$t('frontend_resources_name')"
                />
              </gs-field>
            </div>
            <div class="gs-asset-editor__controls">
              <span class="gs-asset-editor__toggle">
                <span>Show to affiliates</span>
                <gd-toggle v-model="article.enabled" :value="article.enabled" />
              </span>
              <button
                type="button"
                class="gs-asset-editor__delete"
                data-action="delete-article"
                title="Delete article"
                aria-label="Delete article"
                @click="openConfirmModal(index, 'deleteArticle')"
              >
                <trash-icon class="w-4 h-4 fill-current" />
              </button>
            </div>
          </header>

          <div class="gs-asset-editor__section">
            <field-error
              :validator="$v.localFunnelResource.articles.$each[index].article"
              :attribute="$t('frontend_article')"
            >
              <template #default="{ hasErrors, message }">
                <gs-field :label="$t('frontend_resources_article')" :error="message">
                  <textarea
                    v-model="article.article"
                    class="gs-textarea"
                    :class="{ 'has-error': hasErrors }"
                    :placeholder="$t('frontend_resources_article')"
                  ></textarea>
                </gs-field>
              </template>
            </field-error>
            <div class="gs-asset-editor__foot">
              <span class="gs-asset-editor__count">{{ characterCount(article.article) }}</span>
              <gs-name-select placeholder="Insert" max-height="min(280px, 40vh)" :actions="articleInsertActions(index)" size="action" />
            </div>
          </div>
        </section>
      </template>

      <div v-if="hasArticles && !selectedArticle" class="gs-asset-empty">
        <p>Select an article from the list to edit it.</p>
      </div>
      <asset-item-list
        title="Articles"
        :items="articles"
        :selected-id="selectedArticleId"
        :add-label="$t('frontend_resources_add_new')"
        name-fallback="Untitled article"
        :empty-copy="$t('frontend_resources_you_currently_dont_have_articles')"
        enabled-key="enabled"
        :error-ids="articlesErrorIds"
        @select="selectArticle"
        @add="createArticle()"
        @reorder="reorderArticles"
      />
    </div>

    <gs-alert-dialog
      v-model="confirmOpen"
      :title="confirmTitle"
      :copy="$t('frontend_non_revertable')"
      :confirm-label="$t('frontend_delete_confirm')"
      @confirm="applyAction()"
    />
  </div>
</template>

<script>
import generateNumberMixin from '@/mixins/generateNumberMixin';
import { required, minLength, maxLength } from 'vuelidate/lib/validators';
import TrashIcon from '@/assets/images/icons/TrashIcon.svg';
import AssetItemList from './AssetItemList.vue';
import AssetSegmented from './AssetSegmented.vue';
import FieldError from './FieldError.vue';
import { toTabOrder, sortByTabOrder, idFromTabValue } from './tabOrder';

export default {
  components: {
    AssetItemList,
    AssetSegmented,
    FieldError,
    TrashIcon,
  },
  mixins: [generateNumberMixin],
  props: {
    funnelResource: {
      type: Object,
      required: true,
    },
    landingPages: {
      type: Array,
      required: true,
    },
    pressReleasesTab: {
      type: String,
      default: '',
    },
    articlesTab: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      itemToDeleteIndex: null,
      actionType: '',
      confirmOpen: false,
      localFunnelResource: this.funnelResource,
      localPressReleasesTab: this.pressReleasesTab,
      localArticlesTab: this.articlesTab,
      allArticlesTabs: [
        {
          title: this.$t('frontend_resources_press_releases'),
          value: 'pressReleases',
        },
        { title: this.$t('frontend_resources_articles'), value: 'articles' },
      ],
      allArticlesTab: 'articles',
    }
  },
  computed: {
    pressReleases() {
      return (this.localFunnelResource && this.localFunnelResource.press_releases) || [];
    },
    articles() {
      return (this.localFunnelResource && this.localFunnelResource.articles) || [];
    },
    hasPressReleases() {
      return (
        this.localFunnelResource &&
        this.localFunnelResource.press_releases &&
        this.localFunnelResource.press_releases.length
      );
    },
    hasArticles() {
      return (
        this.localFunnelResource &&
        this.localFunnelResource.articles &&
        this.localFunnelResource.articles.length > 0
      );
    },
    selectedPressRelease() {
      return this.pressReleases.filter((release) => this.isPressReleaseTabActive(release))[0] || null;
    },
    selectedPressReleaseId() {
      if (!this.localPressReleasesTab) return null;
      return idFromTabValue('press_release', this.localPressReleasesTab);
    },
    selectedArticle() {
      return this.articles.filter((article) => this.isArticleTabActive(article))[0] || null;
    },
    selectedArticleId() {
      if (!this.localArticlesTab) return null;
      return idFromTabValue('articles', this.localArticlesTab);
    },
    confirmTitle() {
      return this.actionType === 'deleteArticle'
        ? 'Delete this article?'
        : 'Delete this press release?';
    },
    allArticlesErrorTabs() {
      let allArticlesErrorTabs = [];

      if (this.$v.localFunnelResource.press_releases.$error) {
        allArticlesErrorTabs.push('pressReleases');
      }

      if (this.$v.localFunnelResource.articles.$error) {
        allArticlesErrorTabs.push('articles');
      }

      return allArticlesErrorTabs;
    },
    pressReleasesErrorIds() {
      let pressReleasesErrorIds = [];

      let index;
      for (index in this.$v.localFunnelResource.press_releases.$each.$iter) {
        if (this.$v.localFunnelResource.press_releases.$each.$iter[index].$error) {
          pressReleasesErrorIds.push(
            this.$v.localFunnelResource.press_releases.$each.$iter[index].$model.id
          );
        }
      }

      return pressReleasesErrorIds;
    },
    articlesErrorIds() {
      let articlesErrorIds = [];

      let index;
      for (index in this.$v.localFunnelResource.articles.$each.$iter) {
        if (this.$v.localFunnelResource.articles.$each.$iter[index].$error) {
          articlesErrorIds.push(
            this.$v.localFunnelResource.articles.$each.$iter[index].$model.id
          );
        }
      }

      return articlesErrorIds;
    },
  },
  mounted() {
    this.$root.$on('trigger-validation', () => {
      this.$v.$touch();
    });
  },
  methods: {
    characterCount(value) {
      const length = (value || '').length;
      return `${length} ${length === 1 ? 'character' : 'characters'}`;
    },
    affiliateLinksActionsPressReleases(index) {
      let affiliateLinksActions = [];

      if (this.landingPages.length > 0) {
        for (let i = 0; i < this.landingPages.length; i++) {
          affiliateLinksActions.push({
            text: this.landingPages[i].description,
            callback: () => this.addAffiliateLinkToPressRelease(index, this.landingPages[i].id),
          });
        }
      }

      return affiliateLinksActions;
    },
    pressReleaseInsertActions(index) {
      return [
        {
          text: this.$t('frontend_resources_add_affiliate_name'),
          callback: () => this.addAffiliateNameToPressRelease(index),
        },
        ...this.affiliateLinksActionsPressReleases(index),
      ];
    },
    affiliateLinksActionsArticles(index) {
      let affiliateLinksActions = [];

      if (this.landingPages.length > 0) {
        for (let i = 0; i < this.landingPages.length; i++) {
          affiliateLinksActions.push({
            text: this.landingPages[i].description,
            callback: () => this.addAffiliateLinkToArticle(index, this.landingPages[i].id),
          });
        }
      }

      return affiliateLinksActions;
    },
    articleInsertActions(index) {
      return [
        {
          text: this.$t('frontend_resources_add_affiliate_name'),
          callback: () => this.addAffiliateNameToArticle(index),
        },
        ...this.affiliateLinksActionsArticles(index),
      ];
    },
    updateFunnelResource() {
      this.$emit('updateFunnelResource', this.localFunnelResource);
    },
    selectPressRelease(id) {
      this.handlePressReleasesTabChange(`press_release_tab_${id}`);
    },
    selectArticle(id) {
      this.handleArticlesTabChange(`articles_tab_${id}`);
    },
    // `AssetItemList` reports the new order as ids; the existing handlers speak
    // the tab shape gd-tabs used, so the ids are wrapped back into it.
    reorderPressReleases(ids) {
      this.handlePressReleaseTabOrderUpdate(toTabOrder('press_release', ids));
    },
    reorderArticles(ids) {
      this.handleArticlesTabOrderUpdate(toTabOrder('articles', ids));
    },
    handlePressReleasesTabChange(newTab) {
      this.localPressReleasesTab = newTab || this.localPressReleasesTab;
      this.$emit('handlePressReleasesTabChange', this.localPressReleasesTab);
    },
    handleArticlesTabChange(newTab) {
      this.localArticlesTab = newTab || this.localArticlesTab;
      this.$emit('handleArticlesTabChange', this.localArticlesTab);
    },
    handleAllArticlesTabChange(newTab) {
      this.allArticlesTab = newTab;
    },
    handlePressReleaseTabOrderUpdate(updatedTabs) {
      sortByTabOrder(this.localFunnelResource.press_releases, 'press_release', updatedTabs);

      this.updateFunnelResource();
    },
    createPressRelease() {
      const newPostId = this.generateNumber();
      const newPostCount = this.localFunnelResource.press_releases.length + 1;

      this.localFunnelResource.press_releases.push({
        id: newPostId,
        name: `${this.$t('frontend_groovesell_tools_affiliate_edit_press_release')} ${newPostCount}`,
        press_release: '',
      });
      this.localPressReleasesTab = `press_release_tab_${newPostId}`;

      this.updateFunnelResource();
      this.handlePressReleasesTabChange();
    },
    openConfirmModal(index, actionType) {
      this.itemToDeleteIndex = index;
      this.actionType = actionType;
      this.confirmOpen = true;
    },
    applyAction() {
      switch (this.actionType) {
        case 'deletePressRelease':
          this.deletePressRelease();
          break;
        case 'deleteArticle':
          this.deleteArticle();
          break;
      }
    },
    async deletePressRelease() {
      this.localFunnelResource.press_releases.splice(this.itemToDeleteIndex, 1);
      this.localPressReleasesTab = '';

      if (this.localFunnelResource.press_releases.length > 0) {
        let nextOpenIndex = 0;
        if (this.itemToDeleteIndex != 0) {
          nextOpenIndex = this.itemToDeleteIndex - 1;
        }

        const nextOpenId = this.localFunnelResource.press_releases[nextOpenIndex].id;
        this.localPressReleasesTab = `press_release_tab_${nextOpenId}`;
      }

      this.updateFunnelResource();
      this.handlePressReleasesTabChange();
    },
    addAffiliateNameToPressRelease(index) {
      this.localFunnelResource.press_releases[
        index
      ].press_release = `${this.localFunnelResource.press_releases[index].press_release}##affiliateName##`;

      this.updateFunnelResource();
    },
    addAffiliateLinkToPressRelease(index, landingPageId) {
      this.localFunnelResource.press_releases[
        index
      ].press_release = `${this.localFunnelResource.press_releases[index].press_release}##affiliateLink${landingPageId}##`;

      this.updateFunnelResource();
    },
    handleArticlesTabOrderUpdate(updatedTabs) {
      sortByTabOrder(this.localFunnelResource.articles, 'articles', updatedTabs);

      this.updateFunnelResource();
    },
    createArticle() {
      const newPostId = this.generateNumber();
      const newPostCount = this.localFunnelResource.articles.length + 1;

      this.localFunnelResource.articles.push({
        id: newPostId,
        name: `${this.$t('frontend_groovesell_tools_affiliate_edit_article')} ${newPostCount}`,
        article: '',
      });

      this.localArticlesTab = `articles_tab_${newPostId}`;

      this.updateFunnelResource();
      this.handleArticlesTabChange();
    },
    async deleteArticle() {
      this.localFunnelResource.articles.splice(this.itemToDeleteIndex, 1);
      this.localArticlesTab = '';

      if (this.localFunnelResource.articles.length > 0) {
        let nextOpenIndex = 0;
        if (this.itemToDeleteIndex != 0) {
          nextOpenIndex = this.itemToDeleteIndex - 1;
        }

        const nextOpenId = this.localFunnelResource.articles[nextOpenIndex].id;
        this.localArticlesTab = `articles_tab_${nextOpenId}`;
      }

      this.updateFunnelResource();
      this.handleArticlesTabChange();
    },
    addAffiliateNameToArticle(index) {
      this.localFunnelResource.articles[
        index
      ].article = `${this.localFunnelResource.articles[index].article}##affiliateName##`;

      this.updateFunnelResource();
    },
    addAffiliateLinkToArticle(index, landingPageId) {
      this.localFunnelResource.articles[
        index
      ].article = `${this.localFunnelResource.articles[index].article}##affiliateLink${landingPageId}##`;

      this.updateFunnelResource();
    },
    isPressReleaseTabActive(release) {
      return this.localPressReleasesTab === `press_release_tab_${release.id}`;
    },
    isArticleTabActive(article) {
      return this.localArticlesTab === `articles_tab_${article.id}`;
    },
  },
  validations: {
    localFunnelResource: {
      press_releases: {
        $each: {
          name: {
            required,
            minLength: minLength(3),
            maxLength: maxLength(80),
          },
          press_release: {
            required,
            minLength: minLength(3),
          },
          $trackBy: 'id',
        },
      },
      articles: {
        $each: {
          name: {
            required,
            minLength: minLength(3),
            maxLength: maxLength(80),
          },
          article: {
            required,
            minLength: minLength(3),
          },
          $trackBy: 'id',
        },
      },
    },
  }
};
</script>

<style src="./asset-split.css"></style>
